import React, { useState, useEffect, useContext, useRef } from 'react';
import { connect } from 'react-redux';
import { notification } from 'antd';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { WidgetContext } from '@/WidgetContextProvider';
import {
	thunkFetchHomescreenWidgets,
	thunkFetchChecklists,
	clearChecklists,
	setWidgets,
} from '@/redux/actions';
import { normalizePage } from '@/redux/util';
import {
	createDraftWidget,
	getVisibleWidgets,
	getWidgetKey,
	mergeWidgetsWithDeleted,
} from '@/redux/reducers/widget.reducer';
import { widgetTypes, withDropDefaults } from '@/components/Widgets';
import { widgetRoomPageId } from '@/enums';
import ChecklistEditor from '@/components/ChecklistEditor';
import WidgetBuilder from './WidgetBuilder';
import WidgetBuilderHeader from './WidgetBuilderHeader/WidgetBuilderHeader';
import UnsavedChangesGuard from './UnsavedChangesGuard';
import { reorder } from 'utils';
import './WidgetBuilder.scss';

const createWidgetRoute = 'widgets/POST';
const updateWidgetRoute = 'widgets/:widgetID/PATCH';
const deleteWidgetRoute = 'widgets/:widgetID/DELETE';
// Without this, a save the server never confirms leaves widgets created but out of widget_order.
const WIDGET_SAVE_TIMEOUT_MS = 15000;

export const WidgetBuilderContainer = props => {
	const { event, auth0Token, socket, fetchHomescreenWidgets, widgets, setWidgets, page } = props;
	const { fetchChecklists, resetChecklists } = props;
	const {
		pendingWidgetKeys,
		submittedCreateIds,
		saveState,
		beginPendingWidgets,
		clearPendingWidgets,
	} = useContext(WidgetContext);
	const saveTimeoutRef = useRef(null);
	// Server acks overwrite a widget's props, so edits are refused until the save settles.
	const isSaving = saveState.inFlight;
	const [widgetToUpdate, setWidgetToUpdate] = useState({});
	const [showUpdateOptions, setShowUpdateOptions] = useState(false);
	const [theme, setTheme] = useState({});
	const [hasChanges, setHasChanges] = useState(false);
	const [preview, setPreview] = useState('tablet');
	const [pageName, setPageName] = useState('');
	const [checklistEditor, setChecklistEditor] = useState(null);

	const findWidgetIndex = widgetKey =>
		widgets.findIndex(widget => getWidgetKey(widget) === widgetKey);
	const getPersistedWidgetOrder = widgetList =>
		getVisibleWidgets(widgetList)
			.filter(widget => widget.id)
			.map(widget => widget.id);
	const getWidgetPayload = widget => ({
		type: widget.type,
		props: JSON.stringify(widget.props),
		id: widget.id,
		event_id: event.id,
	});
	const pageID = widgetRoomPageId(page?.id);
	const replaceWidgetAtIndex = (index, widget, from) =>
		setWidgets([...widgets.slice(0, index), widget, ...widgets.slice(index + 1)], from);
	const removeWidgetAtIndex = (index, from) =>
		setWidgets([...widgets.slice(0, index), ...widgets.slice(index + 1)], from);
	const selectWidget = widget => {
		const widgetKey = getWidgetKey(widget);
		if (!widgetKey) return;
		setWidgetToUpdate({ ...widget, widgetKey });
		const widgetsWithNoOptions = ['Spacer'];
		setShowUpdateOptions(!widgetsWithNoOptions.includes(widget.type));
	};
	const syncWidgetSelection = widget => {
		const widgetKey = getWidgetKey(widget);
		if (!widgetKey) {
			handleClose();
			return;
		}
		setWidgetToUpdate(currentWidget =>
			currentWidget.widgetKey === widgetKey ? currentWidget : { ...widget, widgetKey }
		);
	};
	const createNextWidgetList = nextVisibleWidgets =>
		mergeWidgetsWithDeleted(nextVisibleWidgets, widgets);
	const insertVisibleWidget = (index, widget, from) => {
		const visibleWidgets = getVisibleWidgets(widgets);
		setWidgets(
			createNextWidgetList([
				...visibleWidgets.slice(0, index),
				widget,
				...visibleWidgets.slice(index),
			]),
			from
		);
	};

	useEffect(() => {
		window.Beacon('suggest', ['65ce31a90ba13506ce3ba371']);
		return () => {
			window.Beacon('suggest', []);
			setWidgets([]);
			resetWidgetSave();
		};
	}, []);

	useEffect(() => {
		resetWidgetSave();
		setHasChanges(false);
		if (page) {
			setWidgets(page.widgets || []);
			setTheme(page.theme || {});
			setPageName(page.name);
		} else {
			setTheme(event.homescreen_theme);
			fetchHomescreenWidgets(event.id, 'useEffect.WidgetBuilderContainer.index');
		}
	}, [page?.id, event.id]);

	useEffect(() => {
		resetChecklists();
		fetchChecklists(event.id);
	}, [event.id]);

	// Gated on inFlight so unrelated edits can't trigger a save, but still watching widgets: under
	// react-redux 7 the drained pending set can arrive a render before CREATE_WIDGET merges the new
	// ids in, and patching then would write an order that omits them. Delete this once the app is on
	// react-redux 8+, where the two updates are guaranteed to land together.
	useEffect(() => {
		if (!saveState.inFlight || pendingWidgetKeys.length) return;
		if (saveState.failed)
			return failWidgetSave('Some widgets could not be saved, so the page order was not updated.');
		// Only the creates this save submitted matter — a widget edited after clicking Save is dirty
		// but was never part of it. Leave the timeout armed while waiting, so an id that never
		// arrives still surfaces instead of stalling silently.
		if (!submittedCreateIds.every(hasMergedServerId)) return;
		handleWidgetOrder();
	}, [pendingWidgetKeys, submittedCreateIds, saveState, widgets]);

	useEffect(() => {
		if (!widgetToUpdate.widgetKey) return;

		const selectedWidgetIndex = findWidgetIndex(widgetToUpdate.widgetKey);
		if (selectedWidgetIndex !== -1) return;

		const nextSelectedWidget = widgets.find(widget => {
			if (widgetToUpdate.clientId && widget.clientId === widgetToUpdate.clientId) return true;
			if (widgetToUpdate.id && widget.id === widgetToUpdate.id) return true;
			return false;
		});

		if (nextSelectedWidget) syncWidgetSelection(nextSelectedWidget);
		else handleClose();
	}, [widgets]);

	const resetWidgetSave = () => {
		if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
		saveTimeoutRef.current = null;
		clearPendingWidgets();
	};

	const failWidgetSave = description => {
		resetWidgetSave();
		notification.error({ message: 'Save incomplete', description });
	};

	const handleWidgetOrder = () => {
		const widgetOrder = getPersistedWidgetOrder(widgets);
		resetWidgetSave();
		setHasChanges(false);
		if (!page)
			socket.emit(
				'event/:eventID/PATCH',
				{
					id: event.id,
					homescreen_widget_order: JSON.stringify(widgetOrder),
					homescreen_theme: JSON.stringify(theme),
				},
				auth0Token,
				null,
				'widgetHomescreen'
			);
		else {
			const serializedPage = normalizePage({
				id: page.id,
				event_id: event.id,
				name: pageName,
				theme,
			});
			socket.emit(
				'event/:eventID/pages/:pageID/PATCH',
				{
					page: {
						...serializedPage,
						widget_order: JSON.stringify(widgetOrder),
					},
				},
				auth0Token
			);
		}
	};

	const isDirtyWidget = widget =>
		widget.toDelete || widget.toUpdate || (!widget.id && !!widget.type);
	const hasMergedServerId = clientId =>
		widgets.some(widget => widget.clientId === clientId && widget.id);

	const handleSubmit = () => {
		// A second submit would re-emit the same creates as new rows, and let the first save's acks
		// drain the second's pending set. The save timeout guarantees this clears.
		if (isSaving) return;

		const dirtyWidgets = widgets.filter(isDirtyWidget);
		if (!dirtyWidgets.length) {
			handleWidgetOrder();
			return;
		}

		resetWidgetSave();
		beginPendingWidgets(dirtyWidgets);
		saveTimeoutRef.current = setTimeout(
			() =>
				failWidgetSave(
					'The server did not confirm every widget, so the page order was not updated. Reload the page to check what saved before trying again.'
				),
			WIDGET_SAVE_TIMEOUT_MS
		);

		// Iterating the filtered list keeps these branches in step with isDirtyWidget by construction:
		// a widget counted as pending but never emitted would hold the save open until the timeout.
		dirtyWidgets.forEach(widget => {
			const groupIDs = widget.groups || [];
			if (widget.toDelete)
				socket.emit(deleteWidgetRoute, widget.id, event.id, widget.type, auth0Token, pageID);
			else if (widget.toUpdate)
				socket.emit(
					updateWidgetRoute,
					getWidgetPayload(widget),
					event.id,
					groupIDs,
					auth0Token,
					pageID
				);
			else
				socket.emit(
					createWidgetRoute,
					getWidgetPayload(widget),
					widgets.indexOf(widget),
					event.id,
					groupIDs,
					auth0Token,
					widget.clientId,
					pageID
				);
		});
	};

	const handleDragEnd = result => {
		if (isSaving) return;
		const { source, destination, draggableId } = result;

		if (!destination || destination.droppableId !== 'previewer') return;

		if (source.droppableId !== destination.droppableId) {
			const newWidgetTemplate = cloneDeep(
				widgetTypes.find(widget => widget.type === draggableId.split('-')[0])
			);
			if (!newWidgetTemplate) return;

			const newWidget = createDraftWidget(withDropDefaults(newWidgetTemplate, page));
			insertVisibleWidget(destination.index, newWidget, 'index.handleDragEnd.insert');
			selectWidget(newWidget);
		} else {
			const reorderedVisibleWidgets = reorder(
				getVisibleWidgets(widgets),
				result.source.index,
				result.destination.index
			);
			setWidgets(createNextWidgetList(reorderedVisibleWidgets), 'index.handleDragEnd.reorder');
		}

		setHasChanges(true);
	};

	const handleWidgetDelete = widgetKey => {
		if (isSaving) return;
		const indexToRemove = findWidgetIndex(widgetKey);
		if (indexToRemove === -1) return;
		const widget = widgets[indexToRemove];
		if (widget.id) {
			const remainingVisibleWidgets = getVisibleWidgets(widgets).filter(
				candidate => getWidgetKey(candidate) !== widgetKey
			);
			setWidgets(
				[...createNextWidgetList(remainingVisibleWidgets), { ...widget, toDelete: true }],
				'index.handleWidgetDelete'
			);
		} else removeWidgetAtIndex(indexToRemove, 'index.handleWidgetDelete');

		if (widgetToUpdate.widgetKey === widgetKey) handleClose();
		setHasChanges(true);
	};

	const handleWidgetDuplicate = widgetKey => {
		if (isSaving) return;
		const visibleWidgets = getVisibleWidgets(widgets);
		const sourceIndex = visibleWidgets.findIndex(widget => getWidgetKey(widget) === widgetKey);
		if (sourceIndex === -1) return;
		// Not id or clientId, which would give the copy the original's identity.
		const { type, props, groups } = visibleWidgets[sourceIndex];
		const copy = createDraftWidget(cloneDeep({ type, props, groups }));
		insertVisibleWidget(sourceIndex + 1, copy, 'index.handleWidgetDuplicate');
		selectWidget(copy);
		setHasChanges(true);
	};

	const handleWidgetUpdate = widget => selectWidget(widget);

	const handleUpdate = ({ props: propValues, groups, closeOptions = false } = {}) => {
		if (isSaving) return;
		const { widgetKey, id } = widgetToUpdate;
		const index = findWidgetIndex(widgetKey);
		if (index === -1) return;
		const nextProps = propValues || widgetToUpdate.props || widgets[index].props;
		const nextGroups =
			groups !== undefined ? groups : widgetToUpdate.groups || widgets[index].groups;
		if (isEqual(nextProps, widgets[index].props) && isEqual(nextGroups, widgets[index].groups))
			return;
		const widget = {
			...widgets[index],
			...widgetToUpdate,
			props: nextProps,
			groups: nextGroups,
			toUpdate: id ? true : false,
		};

		// changing back to source.src --> to be deleted
		if (widget.props.source && widget.props.source.uri) {
			widget.props.source.src = widget.props.source.uri;
			delete widget.props.source.uri;
		}

		setWidgetToUpdate(widget);
		replaceWidgetAtIndex(index, widget, 'index.handleUpdate');
		setHasChanges(true);
		if (closeOptions) setShowUpdateOptions(false);
	};

	const handleClose = () => {
		setWidgetToUpdate({});
		setShowUpdateOptions(false);
	};

	return (
		<div className="WidgetBuilderContainer">
			<UnsavedChangesGuard hasChanges={hasChanges} isSaving={isSaving} />
			<WidgetBuilderHeader
				page={page}
				handleSubmit={handleSubmit}
				event={event}
				preview={preview}
				setPreview={setPreview}
				hasChanges={hasChanges}
				isSaving={isSaving}
			/>
			<WidgetBuilder
				{...props}
				handleWidgetUpdate={handleWidgetUpdate}
				handleWidgetDelete={handleWidgetDelete}
				handleWidgetDuplicate={handleWidgetDuplicate}
				handleDragEnd={handleDragEnd}
				handleSubmit={handleSubmit}
				isSaving={isSaving}
				theme={theme}
				setTheme={setTheme}
				setHasChanges={setHasChanges}
				preview={preview}
				showUpdateOptions={showUpdateOptions}
				setShowUpdateOptions={setShowUpdateOptions}
				onUpdate={handleUpdate}
				widgetToUpdate={widgetToUpdate}
				setWidgetToUpdate={setWidgetToUpdate}
				handleClose={handleClose}
				pageName={pageName}
				setPageName={setPageName}
				openChecklistEditor={setChecklistEditor}
			/>
			<ChecklistEditor
				{...checklistEditor}
				open={!!checklistEditor}
				onClose={() => setChecklistEditor(null)}
			/>
		</div>
	);
};

const msp = ({ auth, widgets }) => ({
	auth0Token: auth.auth0Token,
	widgets,
});
const mdp = dispatch => ({
	fetchHomescreenWidgets: (eventID, from) => dispatch(thunkFetchHomescreenWidgets(eventID, from)),
	setWidgets: (widgets, from) => dispatch(setWidgets(widgets, from)),
	fetchChecklists: eventID => dispatch(thunkFetchChecklists(eventID)),
	resetChecklists: () => dispatch(clearChecklists()),
});

export default connect(msp, mdp)(WidgetBuilderContainer);
